<template>
  <div>
    <v-row align="center" class="pa-4 pb-2" dense>
      <v-card-title class="py-0">{{ title }}</v-card-title>
      <v-spacer />
      <v-tooltip bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            color="#4C6C93"
            class="border card-footer mt-1 mb-2"
            v-bind="attrs"
            v-on="on"
            @click="loadHistory"
          >
            <v-icon style="font-size: 40px; height: inherit;" color="white">mdi-reload</v-icon>
          </v-btn>
        </template>
        <span>Обновить</span>
      </v-tooltip>
    </v-row>
    <v-row class="px-4 pb-3" dense>
      <v-col cols="12" sm="5" md="4" lg="3" class="py-0">
        <v-select
          v-model="eventFilter"
          :items="eventFilterItems"
          placeholder="Тип события"
          clearable
          dense
          outlined
          rounded
          hide-details
          prepend-inner-icon="mdi-filter-variant"
          @input="onFilterChange"
        />
      </v-col>
      <v-col v-if="showGlobalFilters" cols="12" sm="5" md="4" lg="2" class="py-0">
        <v-text-field
          v-model="doorphoneIdFilter"
          placeholder="ID домофона"
          clearable
          dense
          outlined
          rounded
          hide-details
          type="text"
          inputmode="numeric"
          prepend-inner-icon="mdi-pound"
          @keyup.enter="onFilterChange"
          @click:clear="onFilterChange"
        />
      </v-col>
      <v-col v-if="showGlobalFilters" cols="12" sm="5" md="4" lg="3" class="py-0">
        <v-text-field
          v-model="addressFilter"
          placeholder="Адрес домофона"
          clearable
          dense
          outlined
          rounded
          hide-details
          prepend-inner-icon="mdi-map-marker-outline"
          @keyup.enter="onFilterChange"
          @click:clear="onFilterChange"
        />
      </v-col>
    </v-row>
    <v-data-table
      class="table"
      :headers="headers"
      :items="rows"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :footer-props="{
        'items-per-page-options': [10, 20, 50, 100],
        showFirstLastPage: true,
      }"
    >
      <template #item.event_at="{ item }">
        {{ ruDateFormat(item.event_at) }}
      </template>
      <template #item.doorphone_address="{ item }">
        <router-link
          v-if="showGlobalFilters && item.doorphone_id && $canRoute('DetailDoorphone')"
          class="routerLink"
          :to="{ name: 'DetailDoorphone', params: { id: String(item.doorphone_id) } }"
        >
          {{ item.doorphone_address }}
        </router-link>
        <span v-else>{{ item.doorphone_address }}</span>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import watchTableOptions from '@/mixins/watchTableOptions';
import { DEFAULT_DATA_TABLE_OPTIONS } from '@/constants/data-table';
import ruDateFormatMixin from '@/mixins/ruDateFormat';

const EVENT_LABELS = {
  manual_set: 'Ручная установка',
  planned_rotate: 'Плановое обновление',
  planned_rotate_run: 'Запуск планового обновления',
};

function rotationText (enabled) {
  if (enabled === true) {
    return 'Вкл.';
  }
  if (enabled === false) {
    return 'Выкл.';
  }
  return null;
}

export default {
  name: 'ServiceDoorCodeHistoryJournal',
  mixins: [watchTableOptions, ruDateFormatMixin],
  props: {
    doorphoneId: {
      type: [Number, String],
      default: null,
    },
    includeDoorphoneAddress: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Журнал сервисного кода',
    },
  },
  data () {
    return {
      loading: false,
      total: 0,
      rawItems: [],
      eventFilter: null,
      addressFilter: '',
      doorphoneIdFilter: '',
      options: {
        ...DEFAULT_DATA_TABLE_OPTIONS,
        itemsPerPage: 20,
      },
      eventFilterItems: [
        { text: 'Ручная установка', value: 'manual_set' },
        { text: 'Плановое обновление', value: 'planned_rotate' },
        { text: 'Запуск планового обновления', value: 'planned_rotate_run' },
      ],
    };
  },
  computed: {
    showGlobalFilters () {
      return this.includeDoorphoneAddress || this.doorphoneId == null || this.doorphoneId === '';
    },
    headers () {
      const base = [
        { text: 'Дата', value: 'event_at', sortable: false },
        { text: 'Событие', value: 'event_label', sortable: false },
        { text: 'Кто', value: 'user_name', sortable: false },
        { text: 'Старый', value: 'old_code', sortable: false },
        { text: 'Новый', value: 'new_code', sortable: false },
        { text: 'Обновление', value: 'rotation_label', sortable: false },
        { text: 'Комментарий', value: 'comment', sortable: false },
        { text: 'Дата обновления', value: 'rotate_at_label', sortable: false },
        { text: 'Изменения', value: 'changes_label', sortable: false },
      ];
      if (this.showGlobalFilters) {
        base.splice(1, 0, { text: 'Адрес / ID', value: 'doorphone_address', sortable: false });
      }
      return base;
    },
    rows () {
      return this.rawItems.map((action) => {
        const data = action.data || {};
        const user = action.user?.user || action.user || {};
        const summary = action.doorphone_summary || {};
        const isBatchRun = data.event === 'planned_rotate_run';
        const rotationEnabled = data.rotation_enabled;
        const rotationLabel = isBatchRun ? '—' : (rotationText(rotationEnabled) || '—');
        const doorphoneId = summary.id || action.doorphone_id;
        let doorphoneAddress = summary.address || '—';
        if (isBatchRun) {
          doorphoneAddress = 'все домофоны';
        } else if (doorphoneId && (!summary.address || summary.address === '—')) {
          doorphoneAddress = `ID ${doorphoneId}`;
        } else if (doorphoneId && summary.address) {
          doorphoneAddress = `${summary.address} (ID ${doorphoneId})`;
        }
        return {
          id: action.id,
          event_at: action.created_at || action.code_received_at,
          event_label: EVENT_LABELS[data.event] || data.event || '—',
          user_name: (data.event === 'planned_rotate' || isBatchRun)
            ? 'Система'
            : (user.name || '—'),
          old_code: isBatchRun ? '—' : (data.old_code ?? '—'),
          new_code: isBatchRun ? '—' : (data.new_code ?? '—'),
          rotation_label: rotationLabel,
          comment: isBatchRun ? '—' : (data.comment || '—'),
          rotate_at_label: isBatchRun
            ? '—'
            : (data.rotate_at
              ? this.ruDateFormat(data.rotate_at)
              : (rotationEnabled === false ? 'не обновлять' : '—')),
          changes_label: this.buildChangesLabel(data),
          doorphone_id: isBatchRun ? null : doorphoneId,
          doorphone_address: doorphoneAddress,
        };
      });
    },
  },
  created () {
    this.loadHistory();
  },
  watch: {
    doorphoneId () {
      this.loadHistory();
    },
    options (curr, prev) {
      this.handleOptionsChange(curr, prev, () => {
        this.loadHistory();
      });
    },
  },
  methods: {
    ...mapActions({
      fetchServiceDoorCodeHistory: 'Doorphone/fetchServiceDoorCodeHistory',
    }),
    buildChangesLabel (data) {
      if (data.event === 'planned_rotate_run') {
        const total = data.total ?? ((data.updated || 0) + (data.skipped || 0) + (data.failed || 0));
        const parts = [
          `обработано: ${total}`,
          `обновлено: ${data.updated ?? 0}`,
        ];
        if ((data.skipped ?? 0) > 0) {
          parts.push(`пропущено: ${data.skipped}`);
        }
        if ((data.failed ?? 0) > 0) {
          parts.push(`ошибок: ${data.failed}`);
        }
        return parts.join('; ');
      }
      const parts = [];
      const oldCode = data.old_code;
      const newCode = data.new_code;
      if (oldCode != null && newCode != null && String(oldCode) !== String(newCode)) {
        parts.push(`код ${oldCode} → ${newCode}`);
      } else if (data.event === 'manual_set' && newCode != null) {
        parts.push(`код ${newCode}`);
      }
      const prevRot = rotationText(data.previous_rotation_enabled);
      const nextRot = rotationText(data.rotation_enabled);
      if (prevRot && nextRot && prevRot !== nextRot) {
        parts.push(`«Обновлять код»: ${prevRot} → ${nextRot}`);
      } else if (nextRot && data.event === 'manual_set') {
        parts.push(`«Обновлять код»: ${nextRot}`);
      }
      const prevAt = data.previous_rotate_at
        ? this.ruDateFormat(data.previous_rotate_at)
        : null;
      const nextAt = data.rotate_at
        ? this.ruDateFormat(data.rotate_at)
        : (data.rotation_enabled === false ? 'не обновлять' : null);
      if (prevAt && nextAt && prevAt !== nextAt) {
        parts.push(`дата: ${prevAt} → ${nextAt}`);
      } else if (nextAt && data.event === 'manual_set') {
        parts.push(`дата: ${nextAt}`);
      } else if (data.lifetime_days != null && data.event === 'manual_set') {
        parts.push(`срок: ${data.lifetime_days} дн.`);
      }
      if (data.event === 'planned_rotate' && nextAt) {
        parts.push(`следующее: ${nextAt}`);
      }
      if (data.comment) {
        parts.push(`комментарий: ${data.comment}`);
      }
      return parts.length ? parts.join('; ') : '—';
    },
    onFilterChange () {
      this.options = { ...this.options, page: 1 };
      this.loadHistory();
    },
    loadHistory () {
      this.loading = true;
      const filters = {};
      if (this.eventFilter) {
        filters.event = this.eventFilter;
      }
      if (this.showGlobalFilters) {
        const idFilter = String(this.doorphoneIdFilter || '').trim();
        if (idFilter) {
          filters.doorphone_id = idFilter;
        }
        if (this.addressFilter) {
          filters.address = this.addressFilter.trim();
        }
      }
      this.fetchServiceDoorCodeHistory({
        doorphoneId: this.doorphoneId,
        page: this.options.page,
        perPage: this.options.itemsPerPage,
        filters,
      })
        .then((data) => {
          this.rawItems = Array.isArray(data?.data) ? data.data : [];
          this.total = data?.meta?.total ?? data?.total ?? this.rawItems.length;
        })
        .catch(() => {
          this.rawItems = [];
          this.total = 0;
          this.$flash_warning('Не удалось загрузить журнал сервисного кода');
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.border {
  border-radius: 16px;
}
.card-footer {
  background-color: #4C6C93;
  color: white;
  width: 65px !important;
  margin-right: 15px;
  height: 65px !important;
  padding: 5px 16px;
  min-width: 65px !important;
}
.routerLink {
  text-decoration: none;
  color: #434850;
}
.routerLink:hover {
  text-decoration: underline;
}
</style>
